/**
 * Sprint 72 — Templates juridiques whitelabel multi-pays.
 *
 * Un tenant peut créer / éditer ses propres templates de convocation,
 * PV AGO/AGE, résolutions et rapport de gestion, par pays (FR/BE/LU).
 * Un template peut être marqué "par défaut" — il sera utilisé automatiquement
 * lors de la génération PDF.
 */
import { useEffect, useMemo, useState } from "react";
import api, { apiError, API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { FileText, Plus, Star, Trash2, Save, Eye, Sparkles, Flag } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  convocation: "Convocation",
  pv_ago: "PV AGO",
  pv_age: "PV AGE",
  resolution_unique: "Résolution associé unique",
  rapport_gestion: "Rapport de gestion",
};
const COUNTRIES = [
  { code: "FR", label: "France", flag: "🇫🇷" },
  { code: "BE", label: "Belgique", flag: "🇧🇪" },
  { code: "LU", label: "Luxembourg", flag: "🇱🇺" },
];

export default function LegalTemplatesPanel() {
  const [items, setItems] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [filterCountry, setFilterCountry] = useState("all");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    type: "convocation", country_code: "FR", name: "",
    content_markdown: "", is_default: false,
  });

  const load = async () => {
    try {
      const params = {};
      if (filterType !== "all") params.type = filterType;
      if (filterCountry !== "all") params.country_code = filterCountry;
      const r = await api.get("/legal/templates", { params });
      setItems(r.data.templates || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [filterType, filterCountry]);

  const seedDefaults = async (country) => {
    try {
      const r = await api.post("/legal/templates/seed-defaults", null,
                               { params: { country_code: country } });
      toast.success(`${r.data.created} template(s) ${country} initialisé(s)`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      type: row.type, country_code: row.country_code, name: row.name,
      content_markdown: row.content_markdown, is_default: !!row.is_default,
    });
  };

  const openCreate = () => {
    setCreating(true);
    setForm({ type: "convocation", country_code: "FR", name: "",
              content_markdown: "", is_default: false });
  };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/legal/templates/${editing.id}`, {
          name: form.name, content_markdown: form.content_markdown,
        });
        toast.success("Template mis à jour");
      } else {
        await api.post("/legal/templates", form);
        toast.success("Template créé");
      }
      setEditing(null); setCreating(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setDefault = async (id) => {
    try {
      await api.post(`/legal/templates/${id}/set-default`);
      toast.success("Défini par défaut");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Supprimer « ${row.name} » ?`)) return;
    try {
      await api.delete(`/legal/templates/${row.id}`);
      toast.success("Template supprimé");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const preview = (id) => {
    // Ouvre le PDF preview inline dans un onglet
    fetch(`${API}/legal/templates/${id}/preview-pdf`, {
      method: "POST", credentials: "include",
    })
      .then((r) => { if (!r.ok) throw new Error("Preview KO"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener");
      })
      .catch(() => toast.error("Aperçu PDF impossible"));
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const it of items) {
      const key = `${it.type}__${it.country_code}`;
      (g[key] = g[key] || []).push(it);
    }
    return g;
  }, [items]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                Templates juridiques whitelabel
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Personnalisez le contenu de vos convocations, PV et résolutions par pays.
                Le template marqué « par défaut » sera utilisé automatiquement lors des exports PDF.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => seedDefaults("FR")}
                       data-testid="seed-fr-btn">
                <Sparkles className="w-3 h-3 mr-1" />Initialiser FR
              </Button>
              <Button variant="outline" size="sm" onClick={() => seedDefaults("BE")}
                       data-testid="seed-be-btn">🇧🇪 Init. BE</Button>
              <Button variant="outline" size="sm" onClick={() => seedDefaults("LU")}
                       data-testid="seed-lu-btn">🇱🇺 Init. LU</Button>
              <Button size="sm" onClick={openCreate} data-testid="new-template-btn">
                <Plus className="w-3 h-3 mr-1" />Nouveau
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-64" data-testid="filter-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCountry} onValueChange={setFilterCountry}>
              <SelectTrigger className="w-48" data-testid="filter-country">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les pays</SelectItem>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.flag} {c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              Aucun template. Cliquez sur « Initialiser FR » pour créer les modèles par défaut.
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(grouped).map(([key, rows]) => {
                const [type, country] = key.split("__");
                const c = COUNTRIES.find((x) => x.code === country);
                return (
                  <div key={key}>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline">{TYPE_LABELS[type] || type}</Badge>
                      <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                        {c?.flag} {c?.label || country}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {rows.map((r) => (
                        <div key={r.id} className="border rounded p-3 flex items-start justify-between hover:bg-slate-50 transition-colors"
                              data-testid={`template-${r.id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium truncate">{r.name}</p>
                              {r.is_default && (
                                <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-xs">
                                  <Star className="w-3 h-3 mr-1" />Défaut
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                              {r.content_markdown.slice(0, 120)}…
                            </p>
                          </div>
                          <div className="flex flex-col gap-1 ml-2">
                            <Button variant="ghost" size="sm" onClick={() => preview(r.id)}
                                     data-testid={`preview-${r.id}`} title="Aperçu PDF">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openEdit(r)}
                                     data-testid={`edit-${r.id}`} title="Éditer">
                              <FileText className="w-4 h-4" />
                            </Button>
                            {!r.is_default && (
                              <Button variant="ghost" size="sm" onClick={() => setDefault(r.id)}
                                       data-testid={`default-${r.id}`} title="Définir par défaut">
                                <Star className="w-4 h-4 text-amber-500" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => remove(r)}
                                     disabled={r.is_default} data-testid={`delete-${r.id}`}
                                     title="Supprimer">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing || creating}
              onOpenChange={(o) => !o && (setEditing(null), setCreating(false))}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Éditer" : "Nouveau"} template</DialogTitle>
            <DialogDescription>
              Utilisez les variables{" "}
              <code className="bg-slate-100 rounded px-1 text-xs">{"{{nom_variable}}"}</code>{" "}
              — elles seront remplacées automatiquement (ex : societe_denomination, date_assemblee…).
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} disabled={!!editing}
                      onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Pays</Label>
              <Select value={form.country_code} disabled={!!editing}
                      onValueChange={(v) => setForm({ ...form, country_code: v })}>
                <SelectTrigger data-testid="form-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.flag} {c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nom</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="form-name" placeholder="Ex : Convocation FR — juridique X" />
            </div>
            <div className="md:col-span-3">
              <Label>Contenu Markdown</Label>
              <Textarea rows={16} value={form.content_markdown}
                        onChange={(e) => setForm({ ...form, content_markdown: e.target.value })}
                        className="font-mono text-xs" data-testid="form-content"
                        placeholder="# Convocation à {{doc_type}}..." />
            </div>
            {!editing && (
              <div className="md:col-span-3 flex items-center gap-2">
                <input type="checkbox" id="is_default" checked={form.is_default}
                        onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                        data-testid="form-default" />
                <Label htmlFor="is_default" className="cursor-pointer">
                  Utiliser ce template par défaut pour {TYPE_LABELS[form.type]} {form.country_code}
                </Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => (setEditing(null), setCreating(false))}>
              Annuler
            </Button>
            <Button onClick={save} disabled={!form.name || form.content_markdown.length < 10}
                    data-testid="save-template-btn">
              <Save className="w-4 h-4 mr-2" />Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
